// components/DatePicker.js
// HST-aware DatePicker that treats the calendar day literally and
// renders/validates in Pacific/Honolulu without shifting days across timezones.

export class DatePicker {
  constructor(button, locale = 'en-US') {
    this.btn = button;
    this.label = button.querySelector('#startDate');
    this.parentDiv = this.btn.closest('div');
    this.blockedDays = new Set();  // array of YYYY-MM-DD in HST
    this.loadedMonths = new Set(); // 'YYYY-MM' whose blocked days are known; others render blurred
    this._silence = false;
    this._iso = null;              // always YYYY-MM-DD (interpreted as HST)
    this.locale = locale || 'en-US';
    this._inited = false;
    this._autoBumpDone = false;    // Run today→next only once
    this.userPicked = false;       // True once the user manually selects a day.
                                   // Lets in-flight auto-pick/auto-bump async
                                   // bail instead of stomping a fresh selection.
  }

  onBeforeOpen = null;

  // Optional callback fired after each draw with the months currently on
  // screen as [{ year, month }] (month is 1-indexed). BookingForm uses it to
  // lazy-load blocked days for whatever months the user has navigated to,
  // instead of pre-fetching a year-plus up front.
  onMonthView = null;

  /* ---------- small helpers ---------- */

  // HST "today" as YYYY-MM-DD (no DST in HST; use UTC-10 math)
  #todayHST() {
    // Exact HST date string YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Pacific/Honolulu',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date());

  }
  
  // Build a human label from an HST YYYY-MM-DD using HST timezone formatting
  #humanFromHST(ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    // 12:00 HST -> 22:00 UTC; safe instant for display
    const utcForNoonHST = Date.UTC(y, m - 1, d, 22, 0, 0);
    // Abbreviated (e.g. "Wed, Sep 24") — the full weekday/month names made the
    // "Selected date …" readout too wide and forced wraps on mobile.
    return new Intl.DateTimeFormat(this.locale, {
      timeZone: 'Pacific/Honolulu',
      weekday: 'short', month: 'short', day: 'numeric'
    }).format(new Date(utcForNoonHST));
  }

  // Read Y-M-D as shown by the calendar grid (local getters) without tz shifts
  #ymdFromLocalDate(dateObj) {
    const pad = n => String(n).padStart(2, '0');
    return `${dateObj.getFullYear()}-${pad(dateObj.getMonth()+1)}-${pad(dateObj.getDate())}`;
  }

  // Set internal value + label + keep Pikaday UI in sync (no tz math)
  #applyYMD(ymd) {
    this._iso = ymd;                  // store literal HST day
    this.btn.dataset.iso = ymd;       // keep legacy API surface
    if (this.label) this.label.textContent = this.#humanFromHST(ymd);

    // keep Pikaday highlighting the same calendar cell (local date with that YMD)
    const [y, m, d] = ymd.split('-').map(Number);
    const localDate = new Date(y, m - 1, d);
    this._silence = true;
    this.picker.setDate(localDate, true);
    this._silence = false;
  }

  #addDaysHST(ymd, n = 1) {
    // Treat ymd as a literal calendar date; hop days via UTC noon to avoid DST/offset weirdness.
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // UTC noon anchor
    dt.setUTCDate(dt.getUTCDate() + n);
    const pad = (x) => String(x).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth()+1)}-${pad(dt.getUTCDate())}`;
  }

  _fixTodayClass() {
    if (!this.picker?.el) return;
    // Clear any local "today"
    this.picker.el.querySelectorAll('td.is-today').forEach(td => td.classList.remove('is-today'));
    // Mark HST today
    const hst = this.#todayHST(); // YYYY-MM-DD
    const [y, m, d] = hst.split('-').map(Number);
    const btn = this.picker.el.querySelector(
      `.pika-button[data-pika-year="${y}"][data-pika-month="${m-1}"][data-pika-day="${d}"]`
    );
    btn?.closest('td')?.classList.add('is-today');
  }

  // Currently-visible month panels as ['YYYY-MM', ...]. Pikaday's `calendars`
  // holds one entry per displayed month; its `month` is 0-indexed.
  visibleMonthKeys() {
    const cals = this.picker?.calendars;
    if (!Array.isArray(cals) || !cals.length) return [];
    return cals.map(c => `${c.year}-${String(c.month + 1).padStart(2, '0')}`);
  }

  // Tell the host which months are on screen. Fired from onDraw, so it covers
  // arrow navigation, gotoDate, and programmatic redraws alike. Never let a
  // listener throw out of a draw.
  _notifyMonthView() {
    if (typeof this.onMonthView !== 'function') return;
    const cals = this.picker?.calendars;
    if (!Array.isArray(cals) || !cals.length) return;
    const months = cals.map(c => ({ year: c.year, month: c.month + 1 }));
    try { this.onMonthView(months); } catch (_) { /* swallow */ }
  }

  // Host tells us which months' blocked days are known. Months not in the set
  // render blurred (see _applyMonthLoadingState) so an unfetched month never
  // looks fully bookable. Re-applied immediately against the current DOM.
  setLoadedMonths(keys) {
    this.loadedMonths = new Set(keys || []);
    this._applyMonthLoadingState();
    this._applyAvailableClass();
  }

  // Toggle a per-panel loading veil: each .pika-lendar maps index-for-index to
  // picker.calendars, so we blur the panels whose month isn't loaded yet.
  _applyMonthLoadingState() {
    if (!this.picker?.el) return;
    const cals = this.picker.calendars;
    if (!Array.isArray(cals)) return;
    const lendars = this.picker.el.querySelectorAll('.pika-lendar');
    lendars.forEach((el, i) => {
      const cal = cals[i];
      if (!cal) return;
      const key = `${cal.year}-${String(cal.month + 1).padStart(2, '0')}`;
      el.classList.toggle('is-month-loading', !this.loadedMonths.has(key));
    });
  }

  init() {
    if (this._inited) return;
    this._inited = true;

    // Inline mode: the calendar is surfaced directly in Step 1 (no dropdown).
    // It renders open at all times and never toggles closed on the trigger.
    this.inline = this.btn?.dataset?.inline === '1';

    this.field = document.createElement('input');
    this.field.type = 'hidden';
    this.btn.after(this.field);
    this.field.value = '';

    const isMobile = matchMedia('(max-width: 767px)').matches;
    const todayHST = this.#todayHST();

    // Make sure parent is positioned so the calendar can be absolutely positioned
    if (this.parentDiv) {
      //console.log(this.parentDiv);
      const style = getComputedStyle(this.parentDiv);
      if (style.position === 'static' || !style.position) {
        this.parentDiv.style.position = 'relative';
      }
    }

    this.picker = new window.Pikaday({
      field: this.field,
      trigger: this.btn,

      // CHANGED: render inside the parent div instead of <body>
      container: this.parentDiv || undefined,
      bound: false, // don't auto-attach to body / window

      firstDay: 1,
      // Don't use minDate (it uses local tz and causes off-by-one).
      // We disable past days inside disableDayFn instead.
      numberOfMonths: isMobile ? 1 : 2,

      //bound: true, // auto-attach to body / window
      // position: isMobile ? 'bottom left' : 'bottom right',
      // reposition: false,
      onDraw: () => { this._fixTodayClass(); this._notifyMonthView(); this._applyMonthLoadingState(); this._applyAvailableClass(); },

      // Disable based on HST today + blocked HST days.
      disableDayFn: (dateObj) => {
        const cellYmd = this.#ymdFromLocalDate(dateObj);
        // Disable if before HST "today"
        if (cellYmd < todayHST) return true;
        // Disable if in blocked list (list is HST YMDs)
        if (this.blockedDays.has(cellYmd)) return true;
        return false;
      },

      onOpen:  () => {
        this.parentDiv?.classList.add('is-open');

        // Also close passengers dropdown if it’s open
        const paxCol = document.querySelector('.nalu-picker-col--passengers');
        if (paxCol) {
          paxCol.classList.remove('is-open');
        }
        //console.log("DatePicker opened");
      },
      onClose: () => {
        // Close date picker UI state
        this.parentDiv?.classList.remove('is-open');

        //console.log("DatePicker closed");
      },

      onSelect: (dateObj) => {
        if (this._silence) return;
        // A genuine user pick. Mark it so any still-in-flight auto-select /
        // auto-bump async knows not to overwrite the user's choice.
        this.userPicked = true;
        // Take the grid’s Y-M-D literally as the intended HST date
        const pickedYmd = this.#ymdFromLocalDate(dateObj);
        this.#applyYMD(pickedYmd);
        // keep the "today" tag correct after a selection change
        this._fixTodayClass();
        // re-mark the available cells so the new selection paints correctly
        this._applyAvailableClass();

        // Inline calendar stays open; only the dropdown variant closes on pick.
        if (!this.inline) {
          if (this.picker && typeof this.picker.hide === 'function') {
            this.picker.hide();
          }
          this.parentDiv?.classList.remove('is-open');
        }
      }
    });

    // Don’t let clicks inside the calendar bubble to “outside click” handlers
    if (this.picker?.el) {
      // Any click inside the calendar should NOT close it
      this.picker.el.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Extra guard: clicking disabled days should never close anything
      this.picker.el.addEventListener('mousedown', (e) => {
        const td = e.target.closest('td');
        if (td && td.classList.contains('is-disabled')) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    // Inline calendar is surfaced open at all times; the dropdown variant
    // starts hidden so it isn't open on modal open.
    if (this.inline) {
      if (this.picker && typeof this.picker.show === 'function') {
        this.picker.show();
      }
      this.parentDiv?.classList.add('is-inline');
    } else {
      if (this.picker && typeof this.picker.hide === 'function') {
        this.picker.hide();
      }
      this.parentDiv?.classList.remove('is-open');
    }

    // Initialize to "today" in HST (display & storage)
    // Initialize selection strictly from HST: prefer server-provided data-iso
    const initialISO = (this.btn.dataset.iso && /^\d{4}-\d{2}-\d{2}$/.test(this.btn.dataset.iso))
      ? this.btn.dataset.iso
      : todayHST;
    this.#applyYMD(initialISO);
    this._fixTodayClass();
    // this.#applyYMD(todayHST);
    // this._fixTodayClass();

    // Switch between two months (desktop) and one month (mobile) when the
    // viewport crosses the breakpoint. Pikaday locks numberOfMonths at
    // construction, so we re-config + adjustCalendars() on change; otherwise a
    // picker built at desktop width keeps two months after resizing to mobile
    // (the second panel float-wraps and leaves dead space on the right).
    this._numMonths = isMobile ? 1 : 2;
    this._mql = matchMedia('(max-width: 767px)');
    const syncMonths = () => {
      const n = this._mql.matches ? 1 : 2;
      if (n === this._numMonths || !this.picker) return;
      this._numMonths = n;
      this.picker.config({ numberOfMonths: n });
      this.picker.adjustCalendars(); // rebuild panels + redraw (fires onDraw)
    };
    if (this._mql.addEventListener) this._mql.addEventListener('change', syncMonths);
    else if (this._mql.addListener) this._mql.addListener(syncMonths); // older Safari

    // Inline mode has no trigger toggle — the readout is non-interactive and
    // the grid is always visible. Only the dropdown variant wires open/close.
    if (!this.inline) {
      // Stop native handlers fighting with our toggle
      const stopAll = (e) => { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation(); };

      this.btn.addEventListener('mousedown', stopAll, { capture: true });
      this.btn.addEventListener('click', (e) => {
        stopAll(e);
        const open = this.picker.isVisible && this.picker.isVisible();
        if (open) {
          this.picker.hide();
        } else {
          setTimeout(() => this.picker.show(), 0);
        }
      }, { capture: true });
    }
  }

  // Paint day cells that are actually bookable. A cell is "available" only when
  // its month's blocked days are known (loadedMonths) AND it isn't past/blocked
  // (Pikaday already flags those as .is-disabled). The loadedMonths guard is
  // load-bearing: without it an un-fetched future month would render every day
  // as available, the same trap the .is-month-loading veil guards against.
  _applyAvailableClass() {
    if (!this.picker?.el) return;
    const today = this.#todayHST();
    this.picker.el.querySelectorAll('td.is-available')
      .forEach(td => td.classList.remove('is-available'));

    const cells = this.picker.el.querySelectorAll('td:not(.is-disabled):not(.is-empty) .pika-button');
    cells.forEach(btn => {
      const y = Number(btn.getAttribute('data-pika-year'));
      const m = Number(btn.getAttribute('data-pika-month')); // 0-indexed
      const d = Number(btn.getAttribute('data-pika-day'));
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return;
      const pad = n => String(n).padStart(2, '0');
      const key = `${y}-${pad(m + 1)}`;
      if (!this.loadedMonths.has(key)) return;          // month not fetched yet
      const ymd = `${key}-${pad(d)}`;
      if (ymd < today) return;                          // past
      if (this.blockedDays.has(ymd)) return;            // sold out
      btn.closest('td')?.classList.add('is-available');
    });
  }

  setLoading(on) {
    // Inline mode: the calendar's per-month loading veil already signals the
    // fetch, so don't spin/overlay the selected-date readout (it caused a
    // spinner + layout shift on first load).
    if (this.inline) return;
    this.btn.classList.toggle('is-loading', !!on);
    if (this.parentDiv?.classList.contains('is-open')) {
      this.parentDiv.classList.toggle('is-loading', !!on);
    }
  }

  get value() { return this.label?.textContent || ''; }
  /** Always an HST YYYY-MM-DD string */
  get iso()   { return this._iso || this.btn.dataset.iso; }

  /** Programmatic set from HST YYYY-MM-DD (or reset to today if falsy) */
  setISO(iso, { silent = false } = {}) {
    const ymd = iso || this.#todayHST();
    this._silence = !!silent;
    this.#applyYMD(ymd);
    this._silence = false;
  }

  /** Called by BookingForm with blocked days as HST YYYY-MM-DD array */
  setBlockedDays(blockedArray) {
    this.blockedDays = new Set(blockedArray);
    this.picker.draw(); // redraw to apply disabled days
    this._fixTodayClass();

    // If currently sitting on HST today and it’s blocked, bump to tomorrow
    // Run the “today blocked → move forward” only once (initial open), and
    // never once the user has manually chosen a day — a blocked-days load that
    // lands after their pick must not yank the selection.
    if (!this._autoBumpDone && !this.userPicked) {
      const today = this.#todayHST();
      if (this._iso === today && this.blockedDays.has(today)) {
        // Find the first unblocked day within a horizon (e.g., 60 days)
        for (let i = 1; i <= 60; i++) {
          const cand = this.#addDaysHST(today, i);
          if (!this.blockedDays.has(cand)) {
            this.#applyYMD(cand);
            this._fixTodayClass();
            try { window.dispatchEvent(new CustomEvent('nalu:date-bumped')); } catch {}
            break;
          }
        }
      }
      this._autoBumpDone = true;
    }

    // const today = this.#todayHST();
    // if (this._iso === today && this.blockedDays.has(today)) {
    //   const nextISO = this.#addDaysHST(today, 1);
    //   // Update selection, label, data-iso, and Pikaday UI
    //   this.#applyYMD(nextISO);
    //   this._fixTodayClass();

    //   // Optional: tell the host (BookingForm) to invalidate the blocked-days key
    //   try { window.dispatchEvent(new CustomEvent('nalu:date-bumped')); } catch {}
    // }
  }

  // Allow BookingForm to reset the one-time auto-bump on re-open. Also clears
  // the manual-pick flag so the next session's auto-select can run again.
  resetAutoBump() {
    this._autoBumpDone = false;
    this.userPicked = false;
  }
}
